import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AdvancedReportConfig } from '@app/advanced-reports/models/advanced-report-config';
import { GridColumn } from '@entity-framework/entity-grid/state/entity-grid-column';
import { TabExportData } from '@app/advanced-reports/dynamic-reporting/excel-export/advanced-reports-excel-export-orchestration.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';
import { getIntQueryParam } from '@utils/route-utils';
import { pensionContributionsGridColumns } from './pension-contributions-grid-columns';
import { pensionPaymentErrorsGridColumns } from './pension-payment-errors-grid-columns';
import { pensionEnrollmentGridColumns } from './pension-enrollment-grid-columns';
import { PensionContributionsDataProvider } from './state/pension-contributions.data-provider';
import { TabConfig } from '@design/buttons/tab-list/tab-config';
import { combineLatest, firstValueFrom } from 'rxjs';
import { ContributionReasonCode, PensionContributionEmployee, PensionSubmissionError } from './state/pension-contribution-employee.model';
import { PensionEnrollmentRecord } from './state/pension-enrollment-record.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ClosedPeriodOperationType } from '../models/closed-period-operation-type.enum';

type TabView = 'Enrollment data' | 'Contributions' | 'Submission errors' | 'Payment errors';
type PensionRowData = PensionEnrollmentRecord[] | PensionContributionEmployee[] | PensionSubmissionError[];

@UntilDestroy()
@Component({
  templateUrl: './pension-contributions-advanced-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PensionContributionsDataProvider],
  styleUrls: ['./pension-contributions-advanced-view.component.scss'],
  standalone: false
})
export class PensionContributionsAdvancedViewComponent {
  private readonly currentViewSubject = new BehaviorSubject<TabView>('Enrollment data');
  private readonly closedPeriodId: number | null;
  private initialDefaultSet = false;
  readonly inFlightSubject = new BehaviorSubject<boolean>(true);

  protected readonly enrollmentData$: Observable<PensionEnrollmentRecord[]>;
  protected readonly contributionsData$: Observable<PensionContributionEmployee[]>;
  protected readonly submissionErrorsData$: Observable<PensionSubmissionError[]>;
  protected readonly paymentErrorsData$: Observable<PensionSubmissionError[]>;
  protected readonly reasonCodes$: Observable<ContributionReasonCode[]>;
  readonly rowData$: Observable<PensionRowData>;
  readonly config$: Observable<AdvancedReportConfig>;
  readonly tabs$: Observable<TabConfig[]>;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly dataProvider: PensionContributionsDataProvider
  ) {
    this.closedPeriodId = getIntQueryParam(this.route, 'closedPeriodId');

    this.enrollmentData$ = this.initializeEnrollmentData();
    this.contributionsData$ = this.initializeContributionsData();
    this.submissionErrorsData$ = this.initializeSubmissionErrorsData();
    this.paymentErrorsData$ = this.initializePaymentErrorsData();
    this.reasonCodes$ = this.initializeReasonCodes();
    this.rowData$ = this.initializeRowData();
    this.config$ = this.initializeConfig();
    this.tabs$ = this.initializeTabs();
  }

  getTabExportData = async (tabLabel: string): Promise<Partial<TabExportData> | null> => {
    let rowData$: Observable<PensionRowData>;
    let gridColumns: GridColumn[] = [];

    switch (tabLabel) {
      case 'Enrollment data':
        rowData$ = this.enrollmentData$;
        gridColumns = pensionEnrollmentGridColumns();
        break;
      case 'Contributions': {
        rowData$ = this.contributionsData$;
        const [contributions, reasonCodes] = await Promise.all([
          firstValueFrom(this.contributionsData$),
          firstValueFrom(this.reasonCodes$)
        ]);
        gridColumns = this.getContributionsGridColumns(contributions ?? [], reasonCodes ?? []);
        break;
      }
      case 'Submission errors':
        rowData$ = this.submissionErrorsData$;
        gridColumns = pensionPaymentErrorsGridColumns();
        break;
      case 'Payment errors':
        rowData$ = this.paymentErrorsData$;
        gridColumns = pensionPaymentErrorsGridColumns();
        break;
      default:
        rowData$ = this.enrollmentData$;
        gridColumns = pensionEnrollmentGridColumns();
    }

    const rowData = await firstValueFrom(rowData$);

    return {
      tabLabel,
      rowData,
      gridColumns
    };
  };

  private initializeEnrollmentData(): Observable<PensionEnrollmentRecord[]> {
    if (!this.closedPeriodId) {
      return of([]);
    }

    return this.dataProvider.getSubmissionEnrollments$(this.closedPeriodId).pipe(
      untilDestroyed(this),
      take(1),
      map((records) => this.sortErrorsFirstThenByName(records, (r) => r.hasErrors)),
      shareReplay(1)
    );
  }

  private initializeContributionsData(): Observable<PensionContributionEmployee[]> {
    if (!this.closedPeriodId) {
      return of([]);
    }

    return this.dataProvider.getSubmissionContributions$(this.closedPeriodId).pipe(
      untilDestroyed(this),
      take(1),
      map((contributions) => this.sortErrorsFirstThenByName(contributions, (c) => c.errors?.length > 0)),
      shareReplay(1)
    );
  }

  private initializeSubmissionErrorsData(): Observable<PensionSubmissionError[]> {
    if (!this.closedPeriodId) {
      return of([]);
    }

    return this.dataProvider
      .getClosedPeriodErrors$(this.closedPeriodId, [ClosedPeriodOperationType.Enrollment, ClosedPeriodOperationType.Contribution])
      .pipe(untilDestroyed(this), take(1), shareReplay(1));
  }

  private initializePaymentErrorsData(): Observable<PensionSubmissionError[]> {
    if (!this.closedPeriodId) {
      return of([]);
    }

    return this.dataProvider
      .getClosedPeriodErrors$(this.closedPeriodId, [ClosedPeriodOperationType.PaymentApproval])
      .pipe(untilDestroyed(this), take(1), shareReplay(1));
  }

  private initializeReasonCodes(): Observable<ContributionReasonCode[]> {
    return this.contributionsData$.pipe(
      switchMap((contributions) => {
        const needsReasonCodes = contributions.some((c) => c.requiresReasonCode);
        if (!needsReasonCodes) return of([] as ContributionReasonCode[]);
        return this.dataProvider.getContributionReasonCodes$();
      }),
      take(1),
      untilDestroyed(this),
      shareReplay(1)
    );
  }

  private initializeRowData(): Observable<PensionRowData> {
    return this.currentViewSubject.pipe(
      untilDestroyed(this),
      tap(() => this.inFlightSubject.next(true)),
      switchMap((view) => {
        switch (view) {
          case 'Enrollment data':
            return this.enrollmentData$;
          case 'Contributions':
            return this.contributionsData$;
          case 'Submission errors':
            return this.submissionErrorsData$;
          case 'Payment errors':
            return this.paymentErrorsData$;
          default:
            return this.enrollmentData$;
        }
      }),
      tap(() => this.inFlightSubject.next(false))
    );
  }

  private initializeConfig(): Observable<AdvancedReportConfig> {
    return this.currentViewSubject.pipe(
      untilDestroyed(this),
      switchMap((view) => {
        const contributions$ = view === 'Contributions' ? this.contributionsData$ : of([] as PensionContributionEmployee[]);
        const codes$ =
          view === 'Contributions' ? this.reasonCodes$.pipe(startWith([] as ContributionReasonCode[])) : of([] as ContributionReasonCode[]);
        return combineLatest([contributions$, codes$]).pipe(map(([contributions, reasonCodes]) => ({ view, contributions, reasonCodes })));
      }),
      map(({ view, contributions, reasonCodes }) => {
        const columns = this.getColumnsForView(view, contributions, reasonCodes);
        const groupedColumn = columns.find((c) => c.colDefOverrides?.rowGroup);

        return {
          gridId: `pension-${view.toLowerCase().replace(/\s+/g, '-')}-v4`,
          reportTitle: 'Pensions connect / Contributions',
          reportType: 'standard' as const,
          gridColumns$: of(columns),
          ...(groupedColumn && {
            autoGroupColumnDef: {
              headerName: groupedColumn.headerLabel,
              width: 300,
              cellRendererParams: { suppressCount: true }
            }
          })
        };
      })
    );
  }

  private initializeTabs(): Observable<TabConfig[]> {
    return combineLatest([
      this.enrollmentData$,
      this.contributionsData$,
      this.submissionErrorsData$,
      this.paymentErrorsData$,
      this.currentViewSubject.asObservable()
    ]).pipe(
      untilDestroyed(this),
      map(([enrollments, contributions, submissionErrors, paymentErrors, currentView]) => {
        const tabs: TabConfig[] = [];

        if (enrollments.length > 0) {
          tabs.push({
            label: 'Enrollment data',
            tid: 'pension-enrollment-data-tab',
            active: false,
            navigate: () => this.switchTab('Enrollment data')
          });
        }

        if (contributions.length > 0) {
          tabs.push({
            label: 'Contributions',
            tid: 'pension-contributions-tab',
            active: false,
            navigate: () => this.switchTab('Contributions')
          });
        }

        if (submissionErrors.length > 0) {
          tabs.push({
            label: 'Submission errors',
            tid: 'pension-submission-errors-tab',
            active: false,
            navigate: () => this.switchTab('Submission errors')
          });
        }

        if (paymentErrors.length > 0) {
          tabs.push({
            label: 'Payment errors',
            tid: 'pension-payment-errors-tab',
            active: false,
            navigate: () => this.switchTab('Payment errors')
          });
        }

        const activeView = this.resolveActiveView(tabs, currentView);

        for (const tab of tabs) {
          tab.active = tab.label === activeView;
        }

        return { tabs, activeView };
      }),
      tap(({ activeView }) => {
        if (this.currentViewSubject.getValue() !== activeView) {
          this.currentViewSubject.next(activeView);
        }
      }),
      map(({ tabs }) => tabs)
    );
  }

  private switchTab(view: TabView): void {
    this.initialDefaultSet = true;
    this.currentViewSubject.next(view);
  }

  private resolveActiveView(tabs: TabConfig[], currentView: TabView): TabView {
    if (!this.initialDefaultSet) {
      this.initialDefaultSet = true;
      return tabs.length > 0 ? this.getDefaultTab(tabs) : currentView;
    }

    const hasCurrentTab = tabs.some((tab) => tab.label === currentView);
    if (hasCurrentTab) {
      return currentView;
    }

    return tabs.length > 0 ? (tabs[0].label as TabView) : currentView;
  }

  private getDefaultTab(tabs: TabConfig[]): TabView {
    const hasPaymentErrors = tabs.some((tab) => tab.label === 'Payment errors');
    if (hasPaymentErrors) {
      return 'Payment errors';
    }
    const hasSubmissionErrors = tabs.some((tab) => tab.label === 'Submission errors');
    if (hasSubmissionErrors) {
      return 'Submission errors';
    }
    const hasContributions = tabs.some((tab) => tab.label === 'Contributions');
    if (hasContributions) {
      return 'Contributions';
    }
    return tabs[0].label as TabView;
  }

  private getColumnsForView(
    view: TabView,
    contributions: PensionContributionEmployee[] = [],
    reasonCodes: ContributionReasonCode[] = []
  ): GridColumn[] {
    switch (view) {
      case 'Enrollment data':
        return pensionEnrollmentGridColumns();
      case 'Contributions':
        return this.getContributionsGridColumns(contributions ?? [], reasonCodes);
      case 'Submission errors':
        return pensionPaymentErrorsGridColumns();
      case 'Payment errors':
        return pensionPaymentErrorsGridColumns();
      default:
        return pensionEnrollmentGridColumns();
    }
  }

  private getContributionsGridColumns(contributions: PensionContributionEmployee[], reasonCodes: ContributionReasonCode[]): GridColumn[] {
    const distinctGroups = new Set(contributions.map((c) => c.employerRefPaymentSource ?? '').filter((g) => g !== '')).size;
    const showEmployerRefPaymentSourceColumn = contributions.some((c) => c.hasEmployerRefPaymentSourceFromApi === true);
    const enableRowGroup = distinctGroups >= 2 && showEmployerRefPaymentSourceColumn;
    return pensionContributionsGridColumns(reasonCodes, enableRowGroup, showEmployerRefPaymentSourceColumn);
  }

  private sortErrorsFirstThenByName<T extends { firstName: string | null; surname: string | null }>(
    items: T[],
    hasErrors: (item: T) => boolean
  ): T[] {
    return items.slice().sort((a, b) => {
      if (hasErrors(a) && !hasErrors(b)) return -1;
      if (!hasErrors(a) && hasErrors(b)) return 1;
      const surnameCompare = (a.surname ?? '').localeCompare(b.surname ?? '');
      return surnameCompare !== 0 ? surnameCompare : (a.firstName ?? '').localeCompare(b.firstName ?? '');
    });
  }
}
